import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useAddOwnerModalStore } from './AddOwnerModalStore';

import { OwnerFormsValues, ownerSchema } from 'api/clients/types';
import { MLSelect } from 'components/Select/Select';
import GreenAddButton from 'components/Button/GreenAddButton';
import useAddOwnerToClient from 'hooks/Object/useAddOwnerToClient';
import useGetClientOwners from 'hooks/Clients/useGetClientOwners';
import CreateOwnerModal from 'pages/ClientDashboard/Modals/CreateOwnerModal';

interface AddOwnerModalProps {
  refetchClients: () => void;
}

const AddOwnerModal: FC<AddOwnerModalProps> = ({ refetchClients }) => {
  const { AddOwnerToClient } = useAddOwnerToClient();
  const { isOpen, clientId, close } = useAddOwnerModalStore();
  const { clientOwners, refetch } = useGetClientOwners();
  const [isOpenOwnerModal, setIsOpenOwnerModal] = useState<boolean>(false);
  const { t } = useTranslation();

  const { handleSubmit, control, reset } = useForm<OwnerFormsValues>({
    resolver: zodResolver(ownerSchema),
    defaultValues: {
      ownerId: '',
    },
  });
  const onReset = () => {
    reset();
  };

  const onAddTechnician = (data: OwnerFormsValues) => {
    AddOwnerToClient(
      {
        clientId: clientId,
        ownerId: data.ownerId,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('Modals.success-edit'), { variant: 'success' });
          close();
          onReset();
          refetchClients();
        },
        onError: (error: any) => {
          if (error.response.data.owner) {
            enqueueSnackbar(error.response.data.owner[0], { variant: 'error' });
          }
          close();
          onReset();
        },
      },
    );
  };

  return (
    <div>
      <Modal isOpen={isOpen} toggle={close} size="md">
        <ModalHeader className="border-bottom pb-2 pt-3 mx-3" toggle={close}>
          {t('Modals.addOwnerModal.title')}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-center col-12">
            <form className="col-12">
              <div className="d-flex gap-2 justify-content-center align-items-end">
                <div className="flex-fill">
                  <Controller
                    control={control}
                    name="ownerId"
                    render={({ field, formState: { errors } }) => (
                      <MLSelect
                        {...field}
                        label={t('Modals.addOwnerModal.owner')}
                        error={errors?.ownerId?.message}
                        value={field.value}
                        required
                        onChange={selectedOption => {
                          field.onChange(selectedOption);
                        }}
                        {...(!clientOwners
                          ? {
                              disabled: true,
                              options: [{ value: '', label: t('Modals.addOwnerModal.noOwners') }],
                            }
                          : {
                              options: [{ value: '', label: t('Modals.addOwnerModal.selectOwner') }].concat(
                                clientOwners?.map(client => ({
                                  value: client.id.toString(),
                                  label: `${client.first_name} ${client.last_name}`,
                                })),
                              ),
                            })}
                      />
                    )}
                  />
                </div>
                <GreenAddButton
                  title={t('Modals.addOwnerModal.createOwner')}
                  onClick={() => setIsOpenOwnerModal(true)}
                />
              </div>
            </form>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-ghost-danger"
            onClick={() => {
              close();
              onReset();
            }}
          >
            {t('Modals.delete-modal.cancel')}
          </Button>{' '}
          <Button className="btn btn-ghost-success" onClick={handleSubmit(onAddTechnician)}>
            {t('Modals.addOwnerModal.add')}
          </Button>
        </ModalFooter>
      </Modal>
      <CreateOwnerModal
        isOpen={isOpenOwnerModal}
        onClose={() => {
          setIsOpenOwnerModal(false);
          refetch();
        }}
      />
    </div>
  );
};

export default AddOwnerModal;
